/**
 * Page chrome shared by every routed page: the small return icon in the header's breadcrumb
 * bar (PageReturn) and the page-action rail on the right edge (PageRail).
 *
 * The header lives in MainLayout, outside the page, so a page tells it where "return" goes
 * through this context instead of drawing its own Back button:
 *
 *   usePageReturn("/batches")                       // a fixed target
 *   usePageReturn(() => { dispatch(x()); navigate(-1); })  // a target with side effects
 *   usePageReturn(null)                             // hide the icon on this page
 *
 * A page that says nothing gets history back (navigate(-1)). The target is reset when the page
 * unmounts, so it never leaks onto the next route.
 */
import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';

const DEFAULT_RETURN = -1;

const PageChromeContext = createContext({
  returnTo: DEFAULT_RETURN,
  setReturnTo: () => {},
  railCount: 0,
  registerRail: () => () => {},
  crumbTail: '',
  setCrumbTail: () => {},
});

export function PageChromeProvider({ children }) {
  const [returnTo, setReturnToState] = useState(DEFAULT_RETURN);
  const [railCount, setRailCount] = useState(0);
  const [crumbTail, setCrumbTail] = useState('');

  // Function targets are stored wrapped so React does not call them as an updater.
  const setReturnTo = useCallback((target) => setReturnToState(() => target), []);
  const registerRail = useCallback(() => {
    setRailCount((n) => n + 1);
    return () => setRailCount((n) => Math.max(0, n - 1));
  }, []);

  const value = useMemo(
    () => ({ returnTo, setReturnTo, railCount, registerRail, crumbTail, setCrumbTail }),
    [returnTo, setReturnTo, railCount, registerRail, crumbTail]
  );
  return <PageChromeContext.Provider value={value}>{children}</PageChromeContext.Provider>;
}

export const usePageChrome = () => useContext(PageChromeContext);

/**
 * Declare where the header's return icon goes on this page.
 * `target`: a path string, a number for history (-1), `{ to, state, replace }`, a function
 * (called on click; does its own navigation), or `null` to hide the icon.
 */
export function usePageReturn(target) {
  const { setReturnTo } = usePageChrome();
  // Keep the latest function without re-registering on every render.
  const latest = useRef(target);
  latest.current = target;
  const isFn = typeof target === 'function';
  const key = isFn ? 'fn' : target === undefined ? 'unset' : JSON.stringify(target);

  useEffect(() => {
    if (target === undefined) return undefined; // nothing declared: leave the default (history back)
    if (isFn) setReturnTo((...args) => latest.current?.(...args));
    else setReturnTo(target);
    return () => setReturnTo(DEFAULT_RETURN);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, setReturnTo]);
}

/**
 * Add a last crumb to the header's breadcrumb for this page (e.g. the open tab: Home / Dashboards / Control Tower / Overview).
 * A page that does not call this is unaffected; the crumb is cleared when the page unmounts.
 */
export function usePageCrumbTail(label) {
  const { setCrumbTail } = usePageChrome();
  useEffect(() => {
    setCrumbTail(label || '');
    return () => setCrumbTail('');
  }, [label, setCrumbTail]);
}
